import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import ContinueButton from '../components/carousel/ContinueButton';
import {
  MediaId,
  photoSectionIdFromUrlSlug,
  PHOTO_DEFINITIONS,
  PHOTO_SECTIONS,
  FILTER_PHOTO_SECTIONS,
  ValidationError,
  PhotoSectionId,
  isValidPhotoSectionsByBrand,
} from '../context/photoConstants';
import { BRAND } from '../static/constants/brands/brand';
import {
  currentZoneAnalisys,
  DispatchFunction,
  IAppContext,
  IState,
  Photo,
  VehicleSide,
} from '../types';
import { AppContext } from '../context/Context';
import {
  deletePhotoFromServer,
  runValidations,
  uploadPhotoToServer,
} from '../helpers/photoUploader';
import { ActionType } from '../context/actions';
import ValidationWarning from '../components/ValidationWarning';
import BackIcon from '../assets/imgs/back.svg?react';
import InfoIcon from '../assets/imgs/i-info.svg?react';
import ViewIcon from '../assets/imgs/i-view.svg?react';
import Modal from '../components/Modal';
import {
  getHelpModalContents,
  refreshGalleryOneScreen,
} from '../helpers/photoHelper';
import ProgressLoading from '../components/common/ProgressLoading';
import {
  getIsValidZones,
  sendToDamageAnalysis,
  shareQuoteLocation,
  UpdateDamageZone,
} from '../services/preInspect.service';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Snackbar,
  TextField,
} from '@mui/material';

import WarningAlertIcon1 from '../assets/imgs/photo-menu/warning-icon-1.svg';
import WarningAlertIcon2 from '../assets/imgs/photo-menu/warning-icon-2.svg';
import InfoAlertIcon from '../assets/imgs/photo-menu/info-icon.svg';
import ErrorAlertIcon from '../assets/imgs/photo-menu/error-icon.svg';
import SuccessAlertIcon from '../assets/imgs/photo-menu/success-icon.svg';
import CameraIcon from '../assets/imgs/photo-menu/cam-icon.svg';
import CloseIcon from '../assets/imgs/close.svg';

import clsx from 'clsx';
import './PhotoSectionScreen.scss';
import { AlertProps } from '@mui/lab/Alert';
type AlertType = 'success' | 'info' | 'warning' | 'warning2' | 'error' | string;
import { Company } from '../interfaces/brand';
import { v4 as uuidv4 } from 'uuid';
import { isNetOnline } from '../helpers/Utils';
import Loading from '../components/common/Loading';
import CameraComponent from '../components/camera/CameraComponent';
import getDeviceBreakpoints from '../utils/deviceBreakpoints';
const USE_CAMERA_MOBILE = import.meta.env.VITE_USE_CAMERA_MOBILE;

function ImagePreviewThumbnail({
  photo,
  onPreviewClick,
  dispatch,
  setValidationError,
}: {
  photo: Photo;
  onPreviewClick?: any;
  dispatch: DispatchFunction;
  setValidationError: any;
}): JSX.Element {
  const photoDefinition = PHOTO_DEFINITIONS[photo.id];

  const handleErrorClick = async () => {
    setValidationError(photo.error);
    //Restablece la URL de la imagen después de completar el proceso
    dispatch({
      type: ActionType.UPDATE_PHOTO_ERROR,
      payload: {
        id: photo.id,
        loading: false,
        error: undefined,
      },
    });
    dispatch({
      type: ActionType.UPDATE_PHOTO_URL,
      payload: {
        id: photo.id,
        uniqueId: photo.uniqueId,
        url: undefined,
      },
    });

    await deletePhotoFromServer(photo.url ?? '');
  };

  return (
    <div className="image-preview-thumbnail">
      {photo.loading && (
        <div>
          <Loading className="loading-thumbnail" />
        </div>
      )}
      <img
        className={photo.error ? 'image-preview-not-valid' : ''}
        src={photo.url}
        alt={photoDefinition.title}
        onClick={() => onPreviewClick(photo)}
      />
      {photoDefinition.shortTitle && (
        <div
          className={clsx(
            'preview-label',
            photo.error ? 'preview-label-not-valid' : '',
          )}
        >
          {photoDefinition.shortTitle}
          {photo.error && <img src={WarningAlertIcon2} />}
        </div>
      )}
      {photo.error && photo.error !== ValidationError.HAS_DAMAGES && (
        <>
          <div className="preview-label-error" onClick={handleErrorClick}>
            {'Ver error imagen'}
          </div>
        </>
      )}
    </div>
  );
}

function ImageUploadThumbnail({
  photo,
  onChange,
  onHelpClick,
  media,
  hasItems = true,
  hasExampleIcon = true,
  setIsExample,
}: {
  photo?: Photo;
  onChange?: any;
  onHelpClick?: any;
  media?: MediaId;
  hasItems?: boolean;
  hasExampleIcon?: boolean;
  setIsExample: any;
}): JSX.Element {
  const photoId = photo?.id ?? media;
  const photoDefinition = PHOTO_DEFINITIONS[photoId!];
  const htmlId = `photo-${photoId}`;

  return (
    <div
      className={clsx(
        'image-upload-thumbnail',
        !hasItems ? 'single-item-thubmanil-width' : '',
      )}
    >
      <label
        htmlFor={hasExampleIcon ? undefined : htmlId}
        className="custom-file-upload"
        onClick={() => {
          {
            setIsExample(false);
            onHelpClick(photoId);
          }
        }}
      >
        {photoDefinition?.placeholder}
        {photoDefinition.title}
      </label>
      {hasExampleIcon ? (
        <div
          className="ayuda"
          onClick={() => {
            {
              setIsExample(true);
              onHelpClick(photoId);
            }
          }}
        >
          <ViewIcon />
          <span>Ejemplo</span>
        </div>
      ) : undefined}
      {photoDefinition.useGallery ? (
        <input
          id={htmlId}
          type="file"
          accept="image/*,video/*"
          onChange={(e) => onChange(photoId, e)}
        />
      ) : (
        <input
          id={htmlId}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => onChange(photoId, e)}
        />
      )}
    </div>
  );
}

function FullScreenPreview({
  url,
  onRemove,
  onExit,
  onRemoveCapture,
  onAcceptDamages,
  hasDamages,
}: {
  url: string;
  onRemove: () => void;
  onExit: () => void;
  onRemoveCapture: () => void;
  onAcceptDamages: (acceptAnalysis: boolean, description?: string) => void;
  hasDamages?: boolean;
}): JSX.Element {
  const [descriptionDamage, setDescriptionDamage] = useState<string>('');
  const [zoomImage, setZoomImage] = useState(false);

  const handleChangeDescription = (event: any) => {
    setDescriptionDamage(event.target.value);
  };

  return hasDamages ? (
    <Modal onBackdropClick={onExit} className="photo-help-modal">
      <div className="close-icon">
        <img src={CloseIcon} onClick={onExit} />
      </div>

      <h2 className={BRAND}>Parece que hay daños en tu vehículo</h2>

      <img
        src={url}
        alt="Photo preview"
        className={zoomImage ? 'photo-help-modal__zoom-image' : ''}
        height={200}
        onClick={() => setZoomImage(!zoomImage)}
      />

      <p style={{ textAlign: 'left', fontSize: '17px' }}>
        Si no estás de acuerdo con este diagnóstico, cuéntanos qué observas. Tu
        respuesta nos ayudará a mejorar.
      </p>

      <p style={{ textAlign: 'left', margin: 0 }}>Agrega una descipción</p>
      <TextField
        className={clsx('preInspection__inputArea')}
        label="Ej: Es el reflejo de un poste"
        value={descriptionDamage}
        onChange={handleChangeDescription}
        multiline
      />

      <div
        style={{
          marginTop: -5,
          marginBottom: 0,
          borderBottom: '1px solid #A3BED6',
        }}
      >
        <ContinueButton
          disabled={!descriptionDamage}
          onClick={() => {
            if (descriptionDamage) {
              onAcceptDamages(false, descriptionDamage);
            }
          }}
        >
          Enviar y continuar
        </ContinueButton>
      </div>

      <p style={{ textAlign: 'left', fontSize: '17px' }}>
        Tambien puedes tomar una nueva foto o aceptar el diagnóstico.
      </p>
      <div className="action-buttons">
        <ContinueButton
          style={{ width: 'auto', margin: '3px' }}
          variant="outlined"
          onClick={onRemoveCapture}
        >
          <img src={CameraIcon} />
        </ContinueButton>
        <ContinueButton
          style={{ margin: '3px' }}
          variant="outlined"
          onClick={() => {
            onAcceptDamages(true);
          }}
        >
          Aceptar diagnóstico
        </ContinueButton>
      </div>
    </Modal>
  ) : (
    <Modal onBackdropClick={onExit} className="photo-help-modal">
      <div className="close-icon">
        <img src={CloseIcon} onClick={onExit} />
      </div>
      <img src={url} alt="Photo preview" height={200} />
      <ContinueButton onClick={onRemoveCapture}>
        Volver a tomar foto
      </ContinueButton>
      <ContinueButton variant="outlined" onClick={onRemove}>
        Eliminar
      </ContinueButton>
    </Modal>
  );
}

export const vehicleZones: VehicleSide[] = [
  {
    id: MediaId.VEHICLE_EXTERIOR_FRONT,
    name: 'FRONTAL',
    sideValidation: 'FRONT-SIDE',
  },
  {
    id: MediaId.VEHICLE_EXTERIOR_RIGHT,
    name: 'LATERAL PASAJERO',
    sideValidation: 'RIGHT-SIDE',
  },
  {
    id: MediaId.VEHICLE_EXTERIOR_RIGHT_FRONT,
    name: 'LATERAL PASAJERO FRONTAL',
    sideValidation: 'RIGHT-FRONT-SIDE',
  },
  {
    id: MediaId.VEHICLE_EXTERIOR_RIGHT_BACK,
    name: 'LATERAL PASAJERO TRASERO',
    sideValidation: 'RIGHT-BACK-SIDE',
  },
  {
    id: MediaId.VEHICLE_EXTERIOR_LEFT,
    name: 'LATERAL CONDUCTOR',
    sideValidation: 'LEFT-SIDE',
  },
  {
    id: MediaId.VEHICLE_EXTERIOR_LEFT_FRONT,
    name: 'LATERAL CONDUCTOR FRONTAL',
    sideValidation: 'LEFT-FRONT-SIDE',
  },
  {
    id: MediaId.VEHICLE_EXTERIOR_LEFT_BACK,
    name: 'LATERAL CONDUCTOR TRASERO',
    sideValidation: 'LEFT-BACK-SIDE',
  },
  {
    id: MediaId.VEHICLE_EXTERIOR_BACK,
    name: 'TRASERO',
    sideValidation: 'BACK-SIDE',
  },
];

interface props {
  idSection?: string;
  onPreviewClick?: any;
}

const PhotoSectionScreen = ({
  idSection,
  onPreviewClick,
}: props): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const { state, dispatch } = useContext(AppContext) as IAppContext;
  const { preInspectionId } = state.preInspectionModel;
  const history = useHistory();
  const [showSectionHelp, setShowSectionHelp] = useState(true);
  const [helpModalId, setHelpModalId] = useState<MediaId | null>(null);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null);
  const [previewPhoto, setPreviewPhoto] = useState<Photo | null>(null);
  const [currentPhotoToAnalysis, setCurrentPhotoToAnalysis] =
    useState<currentZoneAnalisys | null>(null);
  const [currentFileToKeep, setCurrentFileToKeep] = useState<any>(null);
  const [currentMediaSectionId, setCurrentMediaSectionId] =
    useState<MediaId | null>(null);
  const [showValidationConfirm, setShowValidationConfirm] = useState(false);
  const [showZoneValidation, setShowZoneValidation] = useState(false);
  const [descZoneValidation, setDescZoneValidation] = useState('');
  const [alertMessage, setAlertMessage] = useState({
    alertMessage: '',
    alertType: '' as AlertType,
  });
  const [showAlert, setShowAlert] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [showAlertWhenAreNotValid, setShowAlertWhenAreNotValid] =
    useState(false);
  const [showMissingZones, setShowMissingZones] = useState<boolean>(false);
  const [missingZone, setMissingZone] = useState<string>('');
  const [isExample, setIsExample] = useState<boolean>(false);
  const sectionId = idSection ?? id;
  const photoSectionId = photoSectionIdFromUrlSlug(sectionId);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [photoID, setPhotoID] = useState<MediaId | null>(null);
  const photoSection = FILTER_PHOTO_SECTIONS(
    photoSectionId,
    state.preInspectionModel.prePlanType,
  );
  const photos = photoSection.photoIds.map(
    (mediaId) => state.photos[mediaId],
  ) as unknown as Photo[];
  const requiredPhotos = photoSection.photoIds
    .filter(
      (mediaId) =>
        PHOTO_DEFINITIONS[mediaId].isRequired === true &&
        isValidPhotoSectionsByBrand(
          mediaId,
          state.preInspectionModel,
          PHOTO_DEFINITIONS[mediaId].isVisible,
          BRAND,
        ),
    )
    .map((mediaId) => state.photos[mediaId]);

  // Nuevas galerias
  const returnTypeZone = (section: MediaId) => {
    switch (section) {
      case MediaId.VEHICLE_EXTERIOR_BACK:
        return 'TRASERO';
      case MediaId.VEHICLE_EXTERIOR_FRONT:
        return 'FRONTAL';
      case MediaId.VEHICLE_EXTERIOR_LEFT:
        return 'LATERAL CONDUCTOR';
      case MediaId.VEHICLE_EXTERIOR_LEFT_FRONT:
        return 'LATERAL CONDUCTOR FRONTAL';
      case MediaId.VEHICLE_EXTERIOR_LEFT_BACK:
        return 'LATERAL CONDUCTOR TRASERO';
      case MediaId.VEHICLE_EXTERIOR_RIGHT:
        return 'LATERAL PASAJERO';
      case MediaId.VEHICLE_EXTERIOR_RIGHT_FRONT:
        return 'LATERAL PASAJERO FRONTAL';
      case MediaId.VEHICLE_EXTERIOR_RIGHT_BACK:
        return 'LATERAL PASAJERO TRASERO';
      default:
        return null;
    }
  };

  const fetchIconToSeverity = (
    alertType: AlertType,
  ): { icon: string | undefined; color: string | undefined } => {
    switch (alertType) {
      case 'success':
        return { icon: SuccessAlertIcon, color: '#ECF8E8' };
      case 'info':
        return { icon: InfoAlertIcon, color: '#EBF2FF' };
      case 'warning':
        return { icon: WarningAlertIcon1, color: '#FFF8EA' };
      case 'warning2':
        return { icon: WarningAlertIcon2, color: '#FFB41F' };
      case 'error':
        return { icon: ErrorAlertIcon, color: '#FFF6F6' };
      default:
        return { icon: undefined, color: undefined };
    }
  };

  const mapAlertTypeToSeverity = (
    alertType: AlertType,
  ): AlertProps['severity'] => {
    switch (alertType) {
      case 'success':
      case 'info':
      case 'warning':
      case 'error':
        return alertType;
      default:
        return undefined;
    }
  };

  const toggleSectionHelp = () => {
    setShowSectionHelp(!showSectionHelp);
  };

  const onChange = async (
    mediaId: MediaId,
    event: ChangeEvent<HTMLInputElement> | File,
    skipValidation?: boolean,
    photoAttemptId?: string,
    forceUpload?: boolean,
  ) => {
    if (
      (!(event instanceof File) &&
        (!event.target?.files || event.target.files.length === 0)) ||
      !state.preInspectionId
    ) {
      console.warn(
        'No se proporcionó un archivo válido o falta el ID de preinspección',
      );

      return;
    }

    let file: File | undefined;
    if (event instanceof File) {
      file = event;
    } else if (event.target?.files && event.target.files.length > 0) {
      file = event.target.files[0];
    }

    if (!file) {
      console.warn('No se proporcionó un archivo válido');

      return;
    }

    setInProgress(true);

    try {
      const isOnline = await isNetOnline();

      if (!isOnline) {
        console.warn('Not internet connection.');
        setAlertMessage({
          alertMessage:
            'Hemos detectado problemas con tu internet. Verifica tu conexión e intenta de nuevo.',
          alertType: 'warning',
        });

        setShowAlert(true);
        setInProgress(false);
        setLoading(false);

        return;
      } else {
        setShowAlert(false);
      }

      //Get the current photo
      const currentPhotos = { ...state.photos };
      const photoUniqueId = uuidv4();
      const isActiveLocation = ![Company.SURA.toString()].includes(BRAND);

      // Get the local URL from the selected image
      const localImageUrl = URL.createObjectURL(file);
      // Show the image in the async mode
      dispatch({
        type: ActionType.ADD_PHOTO,
        payload: {
          id: mediaId,
          uniqueId: photoAttemptId ? photoAttemptId : photoUniqueId,
          url: localImageUrl,
          loading: true,
        },
      });

      showMessageAlert(
        'Espera un momento, las imágenes se están cargando...',
        'info',
      );

      const photoToProcess = Array.isArray(currentPhotos[mediaId])
        ? ((currentPhotos[mediaId] as Photo[]).slice(-1)[0] as Photo)
        : (currentPhotos[mediaId] as Photo);

      //Check location again
      if (isActiveLocation) {
        if ('geolocation' in navigator) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              dispatch({
                type: ActionType.SET_PREINSPECTION_GEOLOCATION,
                payload: {
                  lat: String(position.coords.latitude),
                  lng: String(position.coords.longitude),
                },
              });

              // Send location to the server
              await shareQuoteLocation(
                state.preInspectionModel.preInspectionId,
                position.coords.latitude,
                position.coords.longitude,
                position.coords.accuracy,
                String(mediaId),
              );
            },
            () => {
              console.warn('Can not get the current location.');
            },
            {
              enableHighAccuracy: true,
              maximumAge: 0, // Avoid using cached locations
            },
          );
        }
      }

      // Send the current photo to API
      const { awsUrl } = await uploadPhotoToServer(
        state.preInspectionId,
        mediaId,
        file,
        skipValidation,
      );

      // Request the validations for the image file
      console.time('Validations');
      const inactiveValidation = [Company.SURA.toString()].includes(BRAND);
      const requireMatch = BRAND === Company.INS || BRAND === Company.LAFISE;
      refreshGalleryOneScreen(dispatch);
      const [error, reduxAction] = await runValidations(
        mediaId,
        awsUrl,
        inactiveValidation,
        state.preInspectionModel.preVehiclePlate,
        requireMatch,
        file,
        forceUpload ?? false,
      );
      console.timeEnd('Validations');
      // Restablece la URL de la imagen después de completar el proceso
      dispatch({
        type: ActionType.UPDATE_PHOTO_URL,
        payload: {
          id: mediaId,
          url: awsUrl,
          uniqueId: photoAttemptId
            ? photoAttemptId
            : photoToProcess.attemps !== undefined &&
              photoToProcess.attemps !== 0 &&
              !photoToProcess.url
            ? photoToProcess.uniqueId
            : photoUniqueId,
        },
      });

      if (!error || (skipValidation !== undefined && skipValidation)) {
        dispatch({
          type: ActionType.ADD_PHOTO,
          payload: {
            id: mediaId,
            url: awsUrl,
            uniqueId: photoAttemptId
              ? photoAttemptId
              : photoToProcess.attemps !== undefined &&
                photoToProcess.attemps !== 0 &&
                !photoToProcess.url
              ? photoToProcess.uniqueId
              : photoUniqueId,
            loading: false,
            error: undefined,
          },
        });

        if (reduxAction) {
          dispatch(reduxAction);
        }

        // Skip the validation if the user confirm use the photo
        if (skipValidation) {
          dispatch({
            type: ActionType.NOTVALID_PHOTO,
            payload: {
              id: mediaId,
              notValid: true,
              uniqueId: photoAttemptId ? photoAttemptId : photoUniqueId,
            },
          });

          dispatch({
            type: ActionType.ATTEMPT_PHOTO,
            payload: {
              id: mediaId,
              attemps: 0,
              uniqueId: photoAttemptId ? photoAttemptId : photoUniqueId,
            },
          });
        } else {
          if (photoToProcess.notValidZone === false) {
            dispatch({
              type: ActionType.NOTVALID_PHOTO,
              payload: {
                id: mediaId,
                notValid: false,
                uniqueId: photoUniqueId,
              },
            });

            dispatch({
              type: ActionType.ATTEMPT_PHOTO,
              payload: {
                id: mediaId,
                attemps: 0,
                isBlurry: false,
                uniqueId: photoUniqueId,
              },
            });
          }
        }

        // Stablish the section for analysis damage
        if (!inactiveValidation) {
          const sectionToAnalysis = returnTypeZone(mediaId);
          setTimeout(() => {
            setCurrentPhotoToAnalysis({
              currentZone: sectionToAnalysis,
              photoId: mediaId,
            });
          }, 100);
        }
      } else {
        const attempsToTry =
          photoToProcess.attemps !== undefined
            ? Number(photoToProcess.attemps) + 1
            : 1;

        // Validate the attemps if the photo is not valid
        let limitAttemps: number =
          mediaId === MediaId.VEHICLE_EXTERIOR_PLATE ||
          mediaId === MediaId.VEHICLE_EXTERIOR_PLATE_CR
            ? 500
            : 2;

        // Validate the limit attemps by Error Type
        limitAttemps =
          error && error === ValidationError.NO_VEHICLE ? 500 : limitAttemps;
        limitAttemps =
          error && error === ValidationError.IS_BLURRY ? 3 : limitAttemps;

        if (
          attempsToTry > limitAttemps ||
          (attempsToTry > 1 && mediaId === MediaId.VEHICLE_INTERIOR_ODOMETER)
        ) {
          dispatch({
            type: ActionType.ATTEMPT_PHOTO,
            payload: {
              id: mediaId,
              attemps: 0,
              uniqueId: photoToProcess
                ? photoToProcess.uniqueId
                : photoUniqueId,
            },
          });

          await deletePhotoFromServer(awsUrl);
          await onChange(mediaId, file, true, photoToProcess.uniqueId);
        } else {
          dispatch({
            type: ActionType.UPDATE_PHOTO_ERROR,
            payload: {
              id: mediaId,
              loading: false,
              error: error,
            },
          });

          dispatch({
            type: ActionType.ATTEMPT_PHOTO,
            payload: {
              id: mediaId,
              attemps: attempsToTry,
              isBlurry: error === ValidationError.IS_BLURRY,
              uniqueId: photoToProcess
                ? photoToProcess.uniqueId
                : photoUniqueId,
            },
          });
        }
      }
    } catch (error) {
      // TODO: Sentry.captureException(error);
      console.error(error);
      setAlertMessage({
        alertMessage:
          'Ocurrió un error no controlado, por favor intente nuevamente.',
        alertType: 'error',
      });

      dispatch({
        type: ActionType.UPDATE_PHOTO_URL,
        payload: { id: mediaId as MediaId, url: undefined },
      });

      dispatch({
        type: ActionType.UPDATE_PHOTO_ERROR,
        payload: {
          id: mediaId,
          loading: false,
          error: undefined,
        },
      });
    } finally {
      setLoading(false);
      setInProgress(false);
    }
  };

  useEffect(() => {
    const countError = errorCountImages(state.photos, false);
    if (countError > 0) {
      const countErrorDamages = errorCountImages(state.photos, true);
      if (countErrorDamages > 0) {
        showMessageAlert(
          `Las fotos resaltadas <b> (${countErrorDamages}) presentan inconsistencias, </b> por favor revísalas y de ser necesario vuelve a tomarlas`,
          'warning2',
        );
      } else {
        showMessageAlert(
          `Las fotos resaltadas <b> (${countError}) no corresponden al ángulo solicitado, </b> por favor revísalas y de ser necesario vuelve a tomarlas`,
          'warning',
        );
      }
    } else {
      setShowAlert(false);
    }

    if (!handleIsDisabled()) {
      showMessageAlert(
        '<b> ¡CARGA EXITOSA! </b> </ br> Todas las fotografías se han cargado correctamente. ¡Bien hecho!',
        'success',
      );
    }
  }, [state.photos]);

  const errorCountImages = (
    photos: {
      [key in MediaId]: Photo | Photo[];
    },
    verifyDamages: boolean,
  ): number => {
    return Object.values(photos).reduce((count, photo) => {
      if (Array.isArray(photo)) {
        return (
          count +
          photo.filter(
            (p) =>
              p.error &&
              (verifyDamages ? p.error === ValidationError.HAS_DAMAGES : true),
          ).length
        );
      } else {
        return (
          count +
          (photo.error &&
          (verifyDamages ? photo.error === ValidationError.HAS_DAMAGES : true)
            ? 1
            : 0)
        );
      }
    }, 0);
  };

  const showMessageAlert = (message: string, alertType: AlertType) => {
    setAlertMessage({
      alertMessage: message,
      alertType: alertType,
    });

    setShowAlert(true);
  };

  const removePhoto = async (id: MediaId, url?: string, uniqueId?: string) => {
    if (url) {
      await deletePhotoFromServer(url);

      if (BRAND === Company.IS && url.includes('VEHICLE_EXTERIOR')) {
        const beforeHasBeenNotify =
          localStorage.getItem('externalCarImagesIsBlurry') === 'true';
        if (beforeHasBeenNotify) {
          let countDeletesByInvalidImages = +(
            localStorage.getItem('countDeletesByInvalidImages') || '0'
          );
          countDeletesByInvalidImages += 1;
          localStorage.setItem(
            'countDeletesByInvalidImages',
            countDeletesByInvalidImages.toString(),
          );

          if (countDeletesByInvalidImages === 3) {
            localStorage.setItem('externalCarImagesIsBlurry', 'false');
            localStorage.setItem('countDeletesByInvalidImages', '0');
          }
        }
      }

      dispatch({
        type: ActionType.REMOVE_PHOTO,
        payload: { id: id, uniqueId: uniqueId },
      });
    }
    setPreviewPhoto(null);
  };

  const handleWarningButton = (): void => {
    setValidationError(null);
  };

  const handleForceUploadButton = (): void => {
    setValidationError(null);
    console.log('force upload', currentMediaSectionId);
  };

  const clearHelpId = (photoId: string) => {
    setHelpModalId(null);
    if (!isExample) {
      const resultEnv = getDeviceBreakpoints();
      if (resultEnv.isDesktop) {
        // Upload from gallery or desktop
        document.getElementById(`photo-${photoId}`)?.click();
      } else {
        if (
          [Company.LAFISE.toString(), Company.IS.toString()].includes(BRAND)
        ) {
          // Upload from camara of mobile
          document.getElementById(`photo-${photoId}`)?.click();
        } else {
          if (USE_CAMERA_MOBILE === 'true') {
            document.getElementById(`photo-${photoId}`)?.click();
          } else {
            // Force capture from camara
            setIsCameraActive(true);
          }
        }
      }
      setPhotoID(photoId as MediaId);
    }
  };

  const handleKeepFile = async () => {
    if (currentMediaSectionId) {
      await onChange(currentMediaSectionId, currentFileToKeep, true);
      setShowValidationConfirm(false);
      setCurrentFileToKeep(null);
      setCurrentMediaSectionId(null);
    }
  };

  const handleCancelKeepFile = async () => {
    setLoading(true);
    setShowValidationConfirm(false);
    if (currentMediaSectionId) {
      const currentPhotos = { ...state.photos };
      const photoToProcess = currentPhotos[currentMediaSectionId];

      if (photoToProcess) {
        dispatch({
          type: ActionType.ATTEMPT_PHOTO,
          payload: {
            id: currentMediaSectionId,
            attemps: 0,
          },
        });

        setCurrentFileToKeep(null);
        setCurrentMediaSectionId(null);
      }
    }
    setLoading(false);
  };

  const handleResultAnalysisBySide = async (
    currentZone: currentZoneAnalisys,
    state: IState,
  ) => {
    // Send to analisys for get damages
    console.log('INIT-DAMAGE-ANALYSIS');

    dispatch({
      type: ActionType.UPDATE_PHOTO_ERROR,
      payload: {
        id: currentZone.photoId,
        loading: true,
        error: undefined,
      },
    });

    setTimeout(() => {
      showMessageAlert(
        'Espera un momento, las imágenes se están analizando...',
        'info',
      );
    }, 100);

    try {
      const resultAnalysis = await sendToDamageAnalysis(
        state,
        currentZone.currentZone || '',
        BRAND,
      );

      const damagesSeverity = resultAnalysis.data?.data?.Damages?.filter(
        (damage: any) => damage.severity !== 'None',
      );

      dispatch({
        type: ActionType.UPDATE_PHOTO_ERROR,
        payload: {
          id: currentZone.photoId,
          loading: false,
          error:
            damagesSeverity?.length > 0
              ? ValidationError.HAS_DAMAGES
              : undefined,
          dataAnalysis:
            damagesSeverity?.length > 0 ? resultAnalysis.data?.data : undefined,
        },
      });
      console.log('FINISH-DAMAGE-ANALYSIS', resultAnalysis);
    } catch {
      setAlertMessage({
        alertMessage:
          'Ocurrió un error no controlado, por favor intente nuevamente.',
        alertType: 'error',
      });

      dispatch({
        type: ActionType.UPDATE_PHOTO_ERROR,
        payload: {
          id: currentZone.photoId,
          loading: false,
          error: undefined,
        },
      });
    }
  };

  const handleVehicleZonesIsValid = async (
    fromSubmit = false,
  ): Promise<boolean> => {
    const inactiveZoneAnalysis = [Company.LAFISE.toString()].includes(BRAND);
    let isValid = true;
    if (photoSectionId === PhotoSectionId.SURA_PHOTOS) {
      const countInvalidZone = (photos as unknown as Photo[]).filter(
        (f) =>
          f.notValidZone === true &&
          (f.notValid === undefined || f.notValid === false),
      );

      // If the external zones validation is success
      if (countInvalidZone.length === 0) {
        setShowZoneValidation(false);
        setDescZoneValidation('');

        if (fromSubmit) {
          isValid = handlePhotosIsValid();
        }
      } else {
        setDescZoneValidation(
          'Las imágenes resaltadas no corresponden a la imagen solicitada, por favor cargarlas nuevamente.',
        );
        setShowZoneValidation(true);

        isValid = false;
      }
    } else if (photoSectionId === PhotoSectionId.INTERIOR) {
      if (fromSubmit && !inactiveZoneAnalysis) {
        const result = await getIsValidZones(preInspectionId);
        if (
          result.isValid === false &&
          result.missingZone &&
          String(result.missingZone).length > 0
        ) {
          isValid = false;
          dispatch({
            type: ActionType.UPDATE_PHOTO_URL,
            payload: { id: result.missingZone as MediaId, url: undefined },
          });

          const zoneName = returnTypeZone(result.missingZone as MediaId);
          if (zoneName) {
            setMissingZone(zoneName);
            setShowMissingZones(true);
          } else {
            setMissingZone('');
            setShowMissingZones(false);
          }
        }
      }
    }

    return isValid;
  };

  const handlePhotosIsValid = (): boolean => {
    if (BRAND === Company.IS) {
      const beforeHasBeenNotify =
        localStorage.getItem('externalCarImagesIsBlurry') === 'true';
      const countExternalZoneIsNotValid = (photos as unknown as Photo[]).filter(
        (p: Photo) =>
          ((p.notValid && p.notValid === true) ||
            (p.isBlurry && p.isBlurry === true)) &&
          [
            PHOTO_DEFINITIONS.VEHICLE_EXTERIOR_FRONT,
            PHOTO_DEFINITIONS.VEHICLE_EXTERIOR_BACK,
            PHOTO_DEFINITIONS.VEHICLE_EXTERIOR_LEFT,
            PHOTO_DEFINITIONS.VEHICLE_EXTERIOR_RIGHT,
          ].includes(PHOTO_DEFINITIONS[p.id]),
      ).length;

      if (countExternalZoneIsNotValid >= 3 || beforeHasBeenNotify) {
        setShowAlertWhenAreNotValid(true);

        return false;
      } else {
        localStorage.setItem('externalCarImagesIsBlurry', 'false');
      }
    }

    dispatch({
      type: ActionType.SET_EXTERNAL_CAR_IMAGES_IS_BLURRY,
      payload: false,
    });

    return true;
  };

  const handleButtonContinue = () => {
    (async (): Promise<void> => {
      if (await handleVehicleZonesIsValid(true)) {
        history.goBack();
      }
    })();
  };

  const handleIsDisabled = (): boolean => {
    // Empty photos
    const hasEmptyPhotos =
      (requiredPhotos as unknown as Photo[]).every(
        (photo) =>
          photo !== undefined &&
          photo.url &&
          String(photo.url).toLowerCase().indexOf('awsaccesskeyid') > -1,
      ) === false;

    // Not valid photos
    const hasNoValidPhotos = (requiredPhotos as unknown as Photo[]).some(
      (photo) =>
        photo.error ||
        (photo.notValidZone &&
          (photo.notValid === undefined || photo.notValid === false)),
    );

    return hasEmptyPhotos || hasNoValidPhotos;
  };

  const handleIsBlurryButtonClose = () => {
    setShowAlertWhenAreNotValid(false);
  };

  const handleIsBlurryButtonContinue = () => {
    dispatch({
      type: ActionType.SET_EXTERNAL_CAR_IMAGES_IS_BLURRY,
      payload: true,
    });
    setShowAlertWhenAreNotValid(false);
    history.goBack();
  };

  const handleIsBlurryClose = (
    _,
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (reason === 'backdropClick') {
      console.log(reason);
    } else {
      setShowAlertWhenAreNotValid(false);
    }
  };

  const handleBackdropClick = (event: any) => {
    //these fail to keep the modal open
    event.stopPropagation();

    return false;
  };

  const handleBackToMenuGallery = (): void => {
    if (handlePhotosIsValid()) {
      history.goBack();
    }
  };

  const handleDeactivateCamera = () => {
    setIsCameraActive(false);
  };

  const handleAnalysis = (file: File): void => {
    if (photoID) {
      onChange(photoID, file);
    }
    // Aquí puedes subir el archivo a un servidor o procesarlo
  };

  const helpContents = getHelpModalContents(helpModalId);

  useEffect(() => {
    (async (): Promise<void> => {
      if (
        currentPhotoToAnalysis?.currentZone !== null &&
        currentPhotoToAnalysis?.currentZone !== undefined &&
        currentPhotoToAnalysis?.currentZone.length > 0
      ) {
        setCurrentPhotoToAnalysis(null);
        await handleResultAnalysisBySide(currentPhotoToAnalysis, state);
      }

      await handleVehicleZonesIsValid();
    })();
  });

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (inProgress) {
        const message =
          'Estás intentando salir de la página mientras se están subiendo imágenes. ¿Estás seguro?';
        event.returnValue = message;

        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [inProgress]);

  useEffect(() => {
    if (showAlert && alertMessage.alertType === 'success') {
      const timeoutId = setTimeout(() => {
        setShowAlert(false);
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  }, [showAlert, alertMessage.alertType]);

  const handleAcceptDamage = async (
    acceptAnalysis: boolean,
    description: string,
    photoId: MediaId,
  ) => {
    console.log('description', description);
    console.log('photoId', photoId);

    await UpdateDamageZone({
      PreInspectionId: state.preInspectionModel.preInspectionId,
      Name: returnTypeZone(photoId) || '',
      DescriptionDamages: description.toUpperCase(),
      AcceptAnalysis: acceptAnalysis,
    });

    dispatch({
      type: ActionType.UPDATE_PHOTO_ERROR,
      payload: {
        id: photoId,
        loading: false,
        error: undefined,
        dataAnalysis: undefined,
      },
    });

    setPreviewPhoto(null);
  };

  return (
    <div className="photo-section-screen">
      {showZoneValidation && (
        <Alert
          className={clsx(
            'vh-side-alert',
            idSection !== undefined ? 'vh-side-alert-top' : '',
          )}
          severity="warning"
        >
          {descZoneValidation}
        </Alert>
      )}
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        className={
          showZoneValidation
            ? 'vh-side-alert-upload'
            : 'vh-side-alert-upload-top'
        }
        open={showAlert}
      >
        <Alert
          onClose={
            alertMessage.alertType === 'success'
              ? () => setShowAlert(false)
              : undefined
          }
          onClick={() => setShowAlert(false)}
          sx={{
            backgroundColor: fetchIconToSeverity(alertMessage.alertType).color,
          }}
          icon={
            <img
              style={{ width: 24, height: 24 }}
              src={fetchIconToSeverity(alertMessage.alertType).icon}
            />
          }
          severity={mapAlertTypeToSeverity(alertMessage.alertType)}
        >
          <div
            dangerouslySetInnerHTML={{ __html: alertMessage.alertMessage }}
          />
        </Alert>
      </Snackbar>
      {showSectionHelp && (
        <Modal onBackdropClick={toggleSectionHelp}>
          <h2>{photoSection.title}</h2>
          {photoSection.helpModal}
          <ContinueButton onClick={toggleSectionHelp}>Entendido</ContinueButton>
        </Modal>
      )}
      {helpModalId && helpContents && (
        <Modal
          onBackdropClick={() => clearHelpId(helpModalId)}
          className="photo-help-modal"
        >
          <h2>{helpContents.title}</h2>
          {helpContents.imgUrl && (
            <img
              src={helpContents?.imgUrl}
              alt={helpContents.title}
              height={200}
            />
          )}
          {helpContents.paragraph}
          <ContinueButton onClick={() => clearHelpId(helpModalId)}>
            Aceptar
          </ContinueButton>
        </Modal>
      )}
      {isCameraActive && (
        <Modal
          onBackdropClick={() => !isCameraActive}
          className="photo-help-modal"
        >
          <CameraComponent
            isCameraActive={isCameraActive}
            onDeactivateCamera={handleDeactivateCamera}
            triggerAnalysis={handleAnalysis}
          />
        </Modal>
      )}
      <Dialog
        className="photo-help-modal"
        open={showValidationConfirm}
        onClose={() => setShowValidationConfirm(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {'Confirmar uso de Fotografía'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Hemos detectado varios intentos de actualización con esta
            fotografía.
            <br />
            <br />
            ¿Deseas confirmar el uso de esta fotografía?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Grid container spacing={2}>
            <Grid item xs={6} md={6}>
              <ContinueButton onClick={handleKeepFile}>
                Sí, guardar
              </ContinueButton>
            </Grid>
            <Grid item xs={6} md={6}>
              <Button onClick={handleCancelKeepFile}>No, cancelar</Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
      <Dialog
        className="photo-help-modal"
        open={showAlertWhenAreNotValid}
        onClose={handleIsBlurryClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        onBackdropClick={handleBackdropClick}
      >
        <DialogTitle id="alert-dialog-title">
          {'Confirmar Fotografías'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <p>
              Hemos identificado que las fotografías externas del vehículo, son{' '}
              <b>imágenes no válidas</b>, ya sea por estar <b>borrosas</b> o por
              estar en <b>zonas no correspondientes</b>.
            </p>
            <p>¿Deseas continuar con el proceso?</p>

            <p className="detail">
              <i>
                <b>
                  NOTA: <u>Si procedes</u> con el uso de estas imágenes, el
                  proceso deberá pasar por otra etapa de validación.
                </b>
              </i>
            </p>
          </DialogContentText>
        </DialogContent>
        <DialogActions className="action-buttons">
          <div className="button-left">
            <Button onClick={handleIsBlurryButtonClose} autoFocus>
              No, cancelar
            </Button>
          </div>
          <div className="button-right">
            <ContinueButton onClick={handleIsBlurryButtonContinue}>
              Proceder
            </ContinueButton>
          </div>
        </DialogActions>
      </Dialog>
      <Dialog
        className="photo-validation-modal"
        open={showMissingZones}
        onClose={() => setShowMissingZones(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {'Validación de Fotografías'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Hemos detectado que la fotografía en la zona <b>{missingZone}</b> no
            fue validada por el sistema.
            <br />
            <br />
            Por favor accede nuevamente a la sección de fotografías externas y
            toma de nuevo la zona recien indicada.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Grid container spacing={2}>
            <Grid item xs={6} md={6}></Grid>
            <Grid item xs={6} md={6}>
              <ContinueButton onClick={() => setShowMissingZones(false)}>
                Entendido
              </ContinueButton>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      {/* {loading && <Loading message="Validando Fotografía" />} */}
      {/** Maybe if we want show all messages for each photo, we will need to define
       *  estimated time for each one and have a variable to track the current photo to
       * change the duration */}
      {loading && <ProgressLoading />}
      {validationError && (
        <ValidationWarning
          validationError={validationError}
          handleWarningButton={handleWarningButton}
          handleForceUploadButton={handleForceUploadButton}
        />
      )}
      <div className="container">
        {!idSection && (
          <h3 className="lead">
            <BackIcon onClick={handleBackToMenuGallery} />
            <span>{PHOTO_SECTIONS[photoSectionId].title}</span>
            <InfoIcon onClick={toggleSectionHelp} />
          </h3>
        )}
        <div className="photos-scrollable-area">
          <div className="photos-container">
            {photos
              .filter((photo: Photo | Photo[]) => {
                if (Array.isArray(photo)) {
                  return true;
                }

                return isValidPhotoSectionsByBrand(
                  photo.id,
                  state.preInspectionModel,
                  PHOTO_DEFINITIONS[photo.id].isVisible,
                  BRAND,
                );
              })
              .map((photo: Photo | Photo[]) => {
                if (Array.isArray(photo)) {
                  return photo.map((p: Photo) => {
                    if (p.url) {
                      return (
                        <ImagePreviewThumbnail
                          key={p.uniqueId}
                          photo={p}
                          onPreviewClick={onPreviewClick ?? setPreviewPhoto}
                          dispatch={dispatch}
                          setValidationError={setValidationError}
                        />
                      );
                    }
                  });
                }
                if (photo.url) {
                  return (
                    <ImagePreviewThumbnail
                      key={photo.id}
                      photo={photo}
                      onPreviewClick={onPreviewClick ?? setPreviewPhoto}
                      dispatch={dispatch}
                      setValidationError={setValidationError}
                    />
                  );
                }
                if (photoSectionId !== PhotoSectionId.ACCESSORIES) {
                  return (
                    <ImageUploadThumbnail
                      key={photo.id}
                      photo={photo}
                      onChange={onChange}
                      setIsExample={setIsExample}
                      onHelpClick={setHelpModalId}
                    />
                  );
                }
              })}
            {photoSectionId === PhotoSectionId.ACCESSORIES && (
              <ImageUploadThumbnail
                key={MediaId.ACCESSORIES}
                media={MediaId.ACCESSORIES}
                onChange={onChange}
                onHelpClick={setHelpModalId}
                hasItems={(photos[0] as unknown as Photo[]).length > 0}
                setIsExample={setIsExample}
                hasExampleIcon={false}
              />
            )}
          </div>
        </div>
      </div>
      {!idSection && (
        <div className="button-panel">
          {/* TODO: Change history.goBack() to a navigation forward based on preinspectionsId */}
          <ContinueButton
            disabled={handleIsDisabled()}
            onClick={handleButtonContinue}
          >
            Continuar
          </ContinueButton>
        </div>
      )}

      {previewPhoto?.url && (
        <FullScreenPreview
          url={
            previewPhoto.error === ValidationError.HAS_DAMAGES
              ? previewPhoto.dataAnalysis?.AnalyzedPhoto
              : previewPhoto.url
          }
          onRemove={() =>
            removePhoto(
              previewPhoto.id,
              previewPhoto.url,
              previewPhoto.uniqueId,
            )
          }
          onExit={() => setPreviewPhoto(null)}
          onRemoveCapture={async () => {
            await removePhoto(
              previewPhoto.id,
              previewPhoto.url,
              previewPhoto.uniqueId,
            );
            clearHelpId(previewPhoto.id);
          }}
          onAcceptDamages={(acceptAnalysis: boolean, description?: string) => {
            handleAcceptDamage(
              acceptAnalysis,
              description || '',
              previewPhoto.id,
            );
          }}
          hasDamages={previewPhoto.error === ValidationError.HAS_DAMAGES}
        />
      )}
    </div>
  );
};

export default PhotoSectionScreen;
